import { useState, useMemo } from 'react';
import { Copy, CheckCircle, AlertTriangle, X, Grid3X3, Users, Layers } from 'lucide-react';
import { useMultiGrid } from '../../hooks/useMultiGrid';
import { useAcademicYear } from '../../contexts/AcademicYearContext';
import { useToast } from '../../contexts/ToastContext';
import { supabase } from '../../supabase/config';
import { StaffAllocationGrid } from '../../types/grid';

interface CloneGridsToYearProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type CloneMode = 'structure_only' | 'with_assignments' | 'empty_template';

export function CloneGridsToYear({ isOpen, onClose, onSuccess }: CloneGridsToYearProps) {
  const { allGrids } = useMultiGrid();
  const { workingYear, availableYears, setWorkingYear } = useAcademicYear();
  const { addToast } = useToast();

  const [sourceYear, setSourceYear] = useState<number>(workingYear);
  const [targetYear, setTargetYear] = useState<number>(workingYear + 1);
  const [selectedGridIds, setSelectedGridIds] = useState<string[]>([]);
  const [cloneMode, setCloneMode] = useState<CloneMode>('structure_only');
  const [isCloning, setIsCloning] = useState(false);

  // Get grids for source year
  const sourceGrids = useMemo(() => {
    return allGrids.filter(g => g.year === sourceYear);
  }, [allGrids, sourceYear]);

  // Check if target year already has grids
  const targetYearHasGrids = useMemo(() => {
    return allGrids.some(g => g.year === targetYear);
  }, [allGrids, targetYear]);

  // Toggle grid selection
  const toggleGridSelection = (gridId: string) => {
    setSelectedGridIds(prev =>
      prev.includes(gridId)
        ? prev.filter(id => id !== gridId)
        : [...prev, gridId]
    );
  };

  // Select all grids
  const selectAllGrids = () => {
    if (selectedGridIds.length === sourceGrids.length) {
      setSelectedGridIds([]);
    } else {
      setSelectedGridIds(sourceGrids.map(g => g.id));
    }
  };

  // Clone a single grid to target year
  const cloneGridToYear = async (grid: StaffAllocationGrid): Promise<void> => {
    // Prepare grid data based on clone mode
    let classes: typeof grid.classes = [];

    switch (cloneMode) {
      case 'with_assignments':
        // Copy everything including teacher assignments
        classes = grid.classes.map(cls => ({
          ...cls,
          id: `${cls.id}_${targetYear}` // Generate new ID
        }));
        break;
      case 'structure_only':
        // Copy classes but remove teacher assignments
        classes = grid.classes.map(cls => ({
          ...cls,
          id: `${cls.id}_${targetYear}`,
          lineAssignments: {} // Clear all assignments
        }));
        break;
      case 'empty_template':
        // Only copy curriculum structure, no classes
        classes = [];
        break;
    }

    const newGridData = {
      name: grid.name,
      description: `Cloned from ${sourceYear} ${grid.name}`,
      grid_data: {
        default_class_minutes: grid.defaultClassMinutes,
        lines: grid.lines,
        study_periods: grid.semesters,
        curriculum_areas: grid.curriculumAreas,
        classes: classes
      },
      classes: classes,
      curriculum_areas: grid.curriculumAreas,
      grid_type_id: grid.gridType?.id || null,
      is_active: true,
      academic_year: targetYear,
      status: 'draft',
      settings: {
        default_class_minutes: grid.defaultClassMinutes,
        lines: grid.lines,
        study_periods: grid.semesters
      }
    };

    const { error } = await supabase
      .from('grids')
      .insert([newGridData]);

    if (error) {
      throw new Error(`Failed to clone ${grid.name}: ${error.message}`);
    }
  };

  // Handle clone operation
  const handleClone = async () => {
    if (selectedGridIds.length === 0) {
      addToast('Please select at least one grid to clone', 'warning');
      return;
    }

    if (targetYear <= sourceYear) {
      addToast('Target year must be greater than source year', 'error');
      return;
    }

    setIsCloning(true);

    try {
      const gridsToClone = sourceGrids.filter(g => selectedGridIds.includes(g.id));
      let successCount = 0;
      const errors: string[] = [];

      for (const grid of gridsToClone) {
        try {
          await cloneGridToYear(grid);
          successCount++;
        } catch (error) {
          errors.push(error instanceof Error ? error.message : `Failed to clone ${grid.name}`);
        }
      }

      if (errors.length > 0) {
        addToast(
          `Cloned ${successCount} grids with ${errors.length} errors. Check console for details.`,
          'warning'
        );
        console.error('Clone errors:', errors);
      } else {
        addToast(`Successfully cloned ${successCount} grids to ${targetYear}`, 'success');
        // Switch to the new year
        setWorkingYear(targetYear);
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      addToast('Failed to clone grids', 'error');
      console.error('Clone error:', error);
    } finally {
      setIsCloning(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Copy className="text-navy-600" />
            Clone Grids to New Year
          </h2>
          <button
            onClick={onClose}
            className="text-ink-400 hover:text-ink-500"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Year Selection */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">
                Source Year
              </label>
              <select
                value={sourceYear}
                onChange={(e) => {
                  setSourceYear(parseInt(e.target.value));
                  setSelectedGridIds([]); // Clear selection when year changes
                }}
                className="w-full px-3 py-2 border border-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500"
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-ink-500">
                {sourceGrids.length} grids available
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">
                Target Year
              </label>
              <select
                value={targetYear}
                onChange={(e) => setTargetYear(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500"
              >
                {[...Array(5)].map((_, i) => {
                  const year = sourceYear + i + 1;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
              {targetYearHasGrids && (
                <p className="mt-1 text-xs text-warn-fg flex items-center gap-1">
                  <AlertTriangle size={12} />
                  This year already has grids
                </p>
              )}
            </div>
          </div>

          {/* Clone Mode */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-ink-700 mb-2">
              Clone Mode
            </label>
            <div className="space-y-2">
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-ink-50">
                <input
                  type="radio"
                  name="cloneMode"
                  value="structure_only"
                  checked={cloneMode === 'structure_only'}
                  onChange={() => setCloneMode('structure_only')}
                  className="mt-1"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <Grid3X3 size={16} className="text-navy-600" />
                    <span className="font-medium">Structure Only</span>
                    <span className="text-xs bg-navy-100 text-navy-700 px-2 py-0.5 rounded">Recommended</span>
                  </div>
                  <p className="text-sm text-ink-500 mt-1">
                    Copy classes, lines, and learning areas. Teacher assignments are cleared.
                  </p>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-ink-50">
                <input
                  type="radio"
                  name="cloneMode"
                  value="with_assignments"
                  checked={cloneMode === 'with_assignments'}
                  onChange={() => setCloneMode('with_assignments')}
                  className="mt-1"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <Users size={16} className="text-ok-fg" />
                    <span className="font-medium">With Assignments</span>
                  </div>
                  <p className="text-sm text-ink-500 mt-1">
                    Copy everything including teacher allocations. Good for minimal changes.
                  </p>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-ink-50">
                <input
                  type="radio"
                  name="cloneMode"
                  value="empty_template"
                  checked={cloneMode === 'empty_template'}
                  onChange={() => setCloneMode('empty_template')}
                  className="mt-1"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <Layers size={16} className="text-navy-600" />
                    <span className="font-medium">Empty Template</span>
                  </div>
                  <p className="text-sm text-ink-500 mt-1">
                    Copy only grid structure with learning areas. No classes or assignments.
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Grid Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-ink-700">
                Select Grids to Clone
              </label>
              <button
                onClick={selectAllGrids}
                className="text-sm text-navy-600 hover:text-navy-800"
              >
                {selectedGridIds.length === sourceGrids.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>

            {sourceGrids.length === 0 ? (
              <div className="text-center py-8 text-ink-500">
                <Grid3X3 className="mx-auto h-12 w-12 text-ink-300 mb-3" />
                <p>No grids found for {sourceYear}</p>
              </div>
            ) : (
              <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                {sourceGrids.map(grid => (
                  <label
                    key={grid.id}
                    className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-ink-50 ${
                      selectedGridIds.includes(grid.id) ? 'bg-navy-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedGridIds.includes(grid.id)}
                      onChange={() => toggleGridSelection(grid.id)}
                      className="h-4 w-4 text-navy-600 rounded"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{grid.name}</span>
                        <span className="font-mono tabular-nums text-xs bg-ink-100 text-ink-500 px-2 py-0.5 rounded">
                          {grid.defaultClassMinutes}min
                        </span>
                      </div>
                      <p className="text-sm text-ink-500">
                        {grid.classes.length} classes
                      </p>
                    </div>
                    {selectedGridIds.includes(grid.id) && (
                      <CheckCircle size={18} className="text-navy-600" />
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-ink-50 flex justify-between items-center">
          <div className="text-sm text-ink-500">
            {selectedGridIds.length} grid(s) selected
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-ink-500 hover:text-ink-900"
            >
              Cancel
            </button>
            <button
              onClick={handleClone}
              disabled={selectedGridIds.length === 0 || isCloning}
              className="px-4 py-2 bg-navy-600 text-white rounded-lg hover:bg-navy-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isCloning ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Cloning...
                </>
              ) : (
                <>
                  <Copy size={16} />
                  Clone to {targetYear}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CloneGridsToYear;
